import { useEffect, useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { schedulerAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtTimestamp(isoString) {
  if (!isoString) return '-';
  const then = new Date(isoString);
  if (Number.isNaN(then.getTime())) return isoString;
  return then.toLocaleString('en-GB');
}

// Diagnostics card: chunked chains (services/chunked_chain.py) that
// chain_watchdog has given up on -- 3 consecutive resume failures, per
// CLAUDE.md Prompt 22 FIX C. Before this existed, a chain in this state was
// invisible: it silently stopped mattering to chain_watchdog (which only
// reclaims status=="running") while still recording the same error to logs
// on every restart forever, which is exactly how invoicing_finalize_apply's
// KeyError('entity_id') went unnoticed as a *chain* problem (as opposed to
// "there's an error in the logs") since 2026-08-12/13.
export function DeadLetterChainsPanel() {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await schedulerAPI.getDeadLetterChains();
      setChains(data.dead_letter_chains || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          Dead-Letter Chains
          {chains.length > 0 && (
            <Badge variant="destructive" data-testid="dead-letter-chains-badge">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {chains.length} stuck
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          A chunked chain that failed 3 consecutive resume attempts stops being auto-retried and
          shows here instead -- otherwise it would silently re-crash on every restart forever. A
          manual re-trigger of the same operation starts a fresh chain.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          data-testid="dead-letter-chains-refresh"
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>

        {loaded && chains.length === 0 && (
          <div className="text-xs text-muted-foreground flex items-center gap-1" data-testid="dead-letter-chains-empty">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            No dead-lettered chains.
          </div>
        )}

        {chains.length > 0 && (
          <div className="overflow-x-auto border rounded-lg max-h-96 overflow-y-auto" data-testid="dead-letter-chains-table">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 sticky top-0">
                  <th className="text-left px-2 py-1 font-medium">Operation</th>
                  <th className="text-left px-2 py-1 font-medium">Chain</th>
                  <th className="text-left px-2 py-1 font-medium">Failures</th>
                  <th className="text-left px-2 py-1 font-medium">Error</th>
                  <th className="text-left px-2 py-1 font-medium">Dead-lettered at</th>
                </tr>
              </thead>
              <tbody>
                {chains.map((c) => (
                  <tr key={c.chain_id} className="border-b last:border-0 bg-destructive/10" data-testid={`dead-letter-row-${c.chain_id}`}>
                    <td className="px-2 py-1 whitespace-nowrap">{c.operation}</td>
                    <td className="px-2 py-1 whitespace-nowrap font-mono">{c.chain_id}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{c.resume_failure_count}</td>
                    <td className="px-2 py-1 max-w-md truncate" title={c.error || ''}>{c.error || '-'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{fmtTimestamp(c.completed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
